// src/components/dubbing/LanguageSelector.js
import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import COLORS from '../../utils/color';

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  width: '100%',
  '& .MuiInputLabel-root': {
    color: COLORS.text.secondary,
    '&.Mui-focused': {
      color: COLORS.primary.main,
    },
  },
  '& .MuiSelect-root': {
    color: COLORS.text.primary,
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: COLORS.overlay.light,
    '& fieldset': {
      borderColor: COLORS.border.primary,
    },
    '&:hover fieldset': {
      borderColor: COLORS.border.focus,
    },
    '&.Mui-focused fieldset': {
      borderColor: COLORS.primary.main,
    },
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  color: COLORS.text.primary,
  '&:hover': {
    backgroundColor: COLORS.hover.background,
  },
  '&.Mui-selected': {
    backgroundColor: `${COLORS.primary.main}20`,
    '&:hover': {
      backgroundColor: `${COLORS.primary.main}30`,
    },
  },
}));

const LanguageSelector = ({
  label,
  value,
  onChange,
  languages = [],
  error = false,
  helperText = '',
  disabled = false,
  showDetect = false
}) => {
  return (
    <StyledFormControl variant="outlined" error={error} disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={value}
        onChange={onChange}
        label={label}
        MenuProps={{
          PaperProps: {
            sx: {
              backgroundColor: COLORS.background.secondary,
              border: `1px solid ${COLORS.border.primary}`,
              '& .MuiList-root': {
                padding: '8px',
              },
            },
          },
        }}
      >
        {showDetect && (
          <StyledMenuItem value="auto">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography>Auto-detect</Typography>
              <Chip 
                label="Recommended" 
                size="small" 
                sx={{ 
                  backgroundColor: `${COLORS.primary.main}20`,
                  color: COLORS.primary.main,
                  fontSize: '11px',
                  height: '20px',
                }}
              />
            </Box>
          </StyledMenuItem>
        )}
        
        {languages.map((lang) => (
          <StyledMenuItem key={lang.code} value={lang.code}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Typography>{lang.name}</Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: COLORS.text.secondary,
                  ml: 2,
                }}
              >
                {lang.code.toUpperCase()}
              </Typography>
            </Box>
          </StyledMenuItem>
        ))}
      </Select>
      
      {helperText && (
        <Typography 
          variant="caption" 
          sx={{ 
            color: error ? COLORS.status.error : COLORS.text.secondary,
            mt: 0.5,
            ml: 1.5,
          }}
        >
          {helperText}
        </Typography>
      )}
    </StyledFormControl>
  );
};

export default LanguageSelector;